import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Icon from '../../components/common/Icon';
import HouseBars from '../../components/common/HouseBars';
import { apiErrorText, apiRequest, teamPhoto } from '../../utils';
import './Leaderboard.css';
import './MemberHome.css';   /* the Bravos vocabulary — bravos-cal__label on the houses heading */
import './MyCompetition.css';

/* One competition day's board, as a COLLEAGUE reads it — GET /api/company/event/<id>/standings.

   ★★ THE SAME BOARD, not a second one. The server reads `fetch_ranking` with the ceremony cap on,
   which is literally the query behind /api/leaderboard and behind the beamer board on the wall.
   So this page wears Leaderboard.css and the very same podium/row markup: three screens showing
   one standing should look like one standing. A second layout would be a second answer to "who is
   first" wearing different clothes.

   ⚠️ THE CAP IS THE POINT, not a limitation to work around. `Event.leaderboardquarter` opens the
   board round by round, and at Hercules the ceremony IS the product. A colleague's phone that ran
   ahead of the room would give the ending away before the host can. The notice at the top says the
   board is still opening, so an early standing never reads as a finished one.

   ★ The houses of THIS DAY sit above the teams, not the all-time standing /my-competition draws.
   Different question, and the heading says which one is being asked.

   ★ And the reader's own line: what they were handed by name, and what came with their team. The
   server guarantees own + via_team === team_total, so the three numbers can sit beside each other
   without anybody being counted twice. */

const formatPoints = (n) => Math.round(Number(n) || 0).toLocaleString();

const teamImage = (team) => {
  const photo = teamPhoto(team.photo_url, team.company_picture);
  const contain = !team.photo_url && !!team.company_picture;
  return { photo, contain };
};

// The platform-wide handover: stash the id, and every team screen reads it back and sends it as
// the `Otherteamid` header. Set on the way out even though /public-team takes the id in its path,
// because the screens that page offers next (edit, players) are the ones that need it.
const stashTeam = (teamId) => {
  if (teamId) sessionStorage.setItem('otherTeamId', teamId);
};

const Slot = ({ team, rank, tone }) => {
  const { photo, contain } = teamImage(team);
  return (
    <Link
      to={`/public-team/${team.team_id}`}
      onClick={() => stashTeam(team.team_id)}
      className={`lb-slot lb-slot--${tone}`}
    >
      <span className="lb-slot__crown">
        <Icon name={rank === 1 ? 'crown' : 'medal'} pack="sr" />
      </span>
      <div className="lb-slot__avatar-wrap">
        <div
          className={`lb-slot__avatar ${contain ? 'lb-slot__avatar--contain' : ''}`}
          style={{ backgroundImage: `url(${photo})` }}
        />
        <span className="lb-slot__rank">{rank}</span>
      </div>
      <p className="lb-slot__name">{team.name}</p>
      {team.company_name && <p className="lb-slot__company">{team.company_name}</p>}
      <div className="lb-slot__points">
        {formatPoints(team.points)}
        <span className="lb-slot__points-label">pts</span>
      </div>
    </Link>
  );
};

const Row = ({ team, rank, isMine }) => {
  const { photo, contain } = teamImage(team);
  return (
    <Link
      to={`/public-team/${team.team_id}`}
      onClick={() => stashTeam(team.team_id)}
      className={`lb-row ${isMine ? 'lb-row--mine' : ''}`}
    >
      {team.house_color && (
        <span className="lb-row__house" style={{ background: team.house_color }} />
      )}
      <div className="lb-row__rank">{rank}</div>
      <div className="lb-row__avatar-wrap">
        <div
          className={`lb-row__avatar ${contain ? 'lb-row__avatar--contain' : ''}`}
          style={{ backgroundImage: `url(${photo})` }}
        />
      </div>
      <div className="lb-row__body">
        <p className="lb-row__name">{team.name}</p>
        {team.company_name && (
          <p className="lb-row__company">
            {team.house_color && !isMine && (
              <span className="lb-row__house-flag" style={{ background: team.house_color }} />
            )}
            {team.company_name}
          </p>
        )}
      </div>
      <div className="lb-row__points">
        <span className="lb-row__points-num">{formatPoints(team.points)}</span>
        <span className="lb-row__points-label">pts</span>
      </div>
    </Link>
  );
};

const CompanyEventStandings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    apiRequest('GET', `/company/event/${eventId}/standings`)
      .then(setData)
      .catch((err) => setError(apiErrorText(t, err)));
  }, [eventId, t]);

  const teams = useMemo(() => (data && data.teams) || [], [data]);
  const me = data && data.me;
  const myTeamId = me && me.team_id;

  const myEntry = useMemo(() => {
    if (!myTeamId || !teams.length) return null;
    const idx = teams.findIndex((tm) => tm.team_id === myTeamId);
    return idx < 0 ? null : { rank: idx + 1, team: teams[idx] };
  }, [teams, myTeamId]);

  const top3 = teams.slice(0, 3);
  const rest = teams.slice(3);
  const myInTop3 = myEntry && myEntry.rank <= 3;

  return (
    <Base activeNav="home">
      <div className="lb">
        <div className="lb-header">
          <button className="lb-header__back" onClick={() => navigate(-1)} aria-label={t('back')}>
            <Icon name="angle-left" />
          </button>
          <h1 className="lb-header__title">
            {(data && data.event && data.event.name) || t('standings')}
          </h1>
        </div>

        {error && <div className="lb-empty">{error}</div>}

        {/* ★★ HOW MUCH OF THE BOARD IS OPEN, in words, because the alternative is a table of
            zeroes that means two completely different things.

            'closed' — nobody has set a reveal and the day has no end date, so `fetch_ranking`
            shows NOTHING. Every team reads 0 even when they have scored. Without this line a
            reader concludes the competition was pointless; with it they know the host has not
            opened the board yet. (Measured on live: exactly this state on a day whose teams were
            on 69-79 points.)
            'partial' — open up to round N, the ceremony running.
            'full'    — the day is over and the cap is moot, so nothing is said. */}
        {data && data.board_state === 'closed' && (
          <div className="lb-notice">
            <Icon name="info" />
            <span>{t('boardNotOpenYet')}</span>
          </div>
        )}
        {data && data.board_state === 'partial' && (
          <div className="lb-notice">
            <Icon name="info" />
            <span>{t('leaderboardQuarters', { quarter: data.leaderboard_quarter })}</span>
          </div>
        )}

        {!data && !error && (
          <>
            <div className="lb-skel-podium" />
            <div className="lb-skel-row" />
            <div className="lb-skel-row" />
          </>
        )}

        {/* This DAY's houses — not the all-time standing. The heading says which. */}
        {data && (data.houses || []).length > 0 && (
          <div className="mycomp-dayhouses">
            <p className="bravos-cal__label">{t('houseStandingToday')}</p>
            <HouseBars rows={data.houses} mineId={me && me.house_id} />
          </div>
        )}

        {data && teams.length === 0 && !error && (
          <div className="lb-empty">{t('noLeaderboardFound')}</div>
        )}

        {top3.length > 0 && (
          <div className="lb-podium">
            <div className="lb-podium__kicker">
              <Icon name="trophy" pack="sr" /> {t('standings')}
            </div>
            <div className="lb-podium__row">
              {top3[1] ? <Slot team={top3[1]} rank={2} tone="silver" /> : <span />}
              {top3[0] && <Slot team={top3[0]} rank={1} tone="gold" />}
              {top3[2] ? <Slot team={top3[2]} rank={3} tone="bronze" /> : <span />}
            </div>
          </div>
        )}

        {myEntry && !myInTop3 && (
          <Link
            to={`/public-team/${myEntry.team.team_id}`}
            onClick={() => stashTeam(myEntry.team.team_id)}
            className="lb-you"
          >
            <div className="lb-you__rank">
              <span className="lb-you__rank-hash">#</span>{myEntry.rank}
            </div>
            <div className="lb-you__body">
              <div className="lb-you__kicker">{t('yourTeam')}</div>
              <p className="lb-you__name">{myEntry.team.name}</p>
              <p className="lb-you__points">
                <strong>{formatPoints(myEntry.team.points)}</strong> {t('points')}
              </p>
            </div>
            <span className="lb-you__arrow"><Icon name="angle-right" /></span>
          </Link>
        )}

        {rest.length > 0 && (
          <>
            {top3.length > 0 && <div className="lb-divider">{t('rank')}</div>}
            <div className="lb-list">
              {rest.map((team, i) => (
                <Row key={team.team_id} team={team} rank={i + 4}
                     isMine={team.team_id === myTeamId} />
              ))}
            </div>
          </>
        )}

        {/* ★ The reader's own line. `own` is what somebody handed THEM by name; `via_team` is the
            remainder that came with the team — NOT the team's total, which already contains their
            own. The server computes both so that own + via_team === team_total exactly. */}
        {me && me.team_id && (
          <div className="mycomp-mine">
            <p className="mycomp-mine__kicker">{t('yourPointsHere')}</p>
            <div className="mycomp-mine__row">
              <span className="mycomp-mine__cell">
                <span className="mycomp-mine__n">{formatPoints(me.own)}</span>
                <span className="mycomp-mine__label">{t('pointsByYou')}</span>
              </span>
              <span className="mycomp-mine__plus">+</span>
              <span className="mycomp-mine__cell">
                <span className="mycomp-mine__n">{formatPoints(me.via_team)}</span>
                <span className="mycomp-mine__label">{t('pointsViaTeam')}</span>
              </span>
              <span className="mycomp-mine__plus">=</span>
              <span className="mycomp-mine__cell mycomp-mine__cell--total">
                <span className="mycomp-mine__n">{formatPoints(me.team_total)}</span>
                <span className="mycomp-mine__label">{me.team_name || t('yourTeam')}</span>
              </span>
            </div>
          </div>
        )}
      </div>
    </Base>
  );
};

export default CompanyEventStandings;
